"use client";

import React from "react";
import { Phone, Mail, MapPin, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const FormItem = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={className || "space-y-2"} {...props} />;
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const contactInfo = [{
  icon: Phone,
  label: "Telefonisch erreichbar",
  value: "+41 76 371 73 78",
  href: "tel:+41441234567",
  description: "Montag–Freitag: 08:30–17:30 Uhr"
}, {
  icon: Mail,
  label: "E-Mail schreiben",
  value: "renata.wandelherz@gmail.com",
  href: "mailto:renata.wandelherz@gmail.com",
  description: "Ich antworte dir innerhalb von 24 Stunden.",
  link: {
    href: "mailto:hallo@wandelherz.com",
    newTab: true
  }
}, {
  icon: MapPin,
  label: "Standort",
  value: "Zürich Stadt, Kreis 6, Schweiz",
  href: "https://maps.google.com/?q=Zürich,+Schweiz",
  description: "\xA0ganzheitliche Heilung",
  link: {
    href: "https://www.google.com/maps/place/wandelherz+-+Reiki+Therapie+und+Energetisches+Heilen+Z%C3%BCrich/@47.3891213,8.5387774,17z/data=!3m1!4b1!4m6!3m5!1s0x4790807e2ff2522d:0x564f1d862e14fc25!8m2!3d47.3891213!4d8.5387774!16s%2Fg%2F1yh5tbjq1?entry=ttu&g_ep=EgoyMDI2MTAwNi4wIKXMDSoASAFQAw%3D%3D",
    newTab: true
  }
}];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="57747" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
          <Send className="text-white size-10" />
        </div>
        <h3 className="text-2xl font-serif font-light text-foreground">Nachricht gesendet!</h3>
        <p className="text-muted-foreground max-w-md text-lg">
          {successMessage || "Vielen Dank für dein Vertrauen. Ich werde mich in Kürze bei dir melden."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/30 text-primary hover:bg-primary/5 hover:text-primary transition-all duration-300">
          Neue Nachricht senden
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Bitte gib deinen Namen ein."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/70 font-sans">Dein Name. <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Vorname Nachname" {...field} className="bg-secondary/30 border-border focus-visible:border-primary focus-visible:ring-primary/10 h-12 text-foreground placeholder:text-muted-foreground/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/70 font-sans">Anliegen (optional)</FormLabel>
              <FormControl>
                <Input placeholder="z.B. Einzelsitzung oder Retreat" {...field} className="bg-secondary/30 border-border focus-visible:border-primary focus-visible:ring-primary/10 h-12 text-foreground placeholder:text-muted-foreground/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "E-Mail-Adresse ist erforderlich.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte gib eine gültige E‑Mail‑Adresse ein."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/70 font-sans">E-Mail-Adresse <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@beispiel.ch" {...field} className="bg-secondary/30 border-border focus-visible:border-primary focus-visible:ring-primary/10 h-12 text-foreground placeholder:text-muted-foreground/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/70 font-sans">Telefonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+41 79 123 45 67" {...field} className="bg-secondary/30 border-border focus-visible:border-primary focus-visible:ring-primary/10 h-12 text-foreground placeholder:text-muted-foreground/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/70 font-sans">Thema deiner Anfrage.</FormLabel>
            <FormControl>
              <Input placeholder="Kurzer Betreff" {...field} className="bg-secondary/30 border-border focus-visible:border-primary focus-visible:ring-primary/10 h-12 text-foreground placeholder:text-muted-foreground/50 transition-all duration-300 rounded-sm" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Bitte gib eine Nachricht ein."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/70 font-sans">Deine Nachricht. <span className="text-primary">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Wie kann ich dich auf deinem Weg unterstützen?" {...field} className="bg-secondary/30 border-border focus-visible:border-primary focus-visible:ring-primary/10 min-h-[160px] text-foreground placeholder:text-muted-foreground/50 resize-none transition-all duration-300 rounded-sm" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="data_privacy_consent" rules={{
      required: "Bitte stimme der Datenschutzerklärung zu."
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-sm border border-border/50 p-4 bg-secondary/10">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 rounded-sm border-primary text-primary focus:ring-primary/20 accent-primary" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm text-muted-foreground font-sans cursor-pointer">
                Ich stimme der Verarbeitung meiner Daten gemäss <Link to="#" className="text-primary hover:underline">Datenschutzerklärung</Link> zu. <span className="text-primary">*</span>
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-medium bg-primary hover:bg-primary/90 text-primary-foreground border-0 shadow-sm hover:shadow-md transition-all duration-300 rounded-sm">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Wird gesendet...
          </> : 'Nachricht senden'}
      </Button>
    </Form>;
}
export default function LetsTalk() {
  return <section id="verbindung-aufnehmen" className="bg-background text-foreground py-20 lg:py-32 relative overflow-hidden isolate">
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/5 rounded-lg blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-secondary/30 rounded-lg blur-[100px] translate-y-1/3 -translate-x-1/4 pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          <div className="lg:col-span-5 space-y-10">
            <div className="space-y-4">
              <h2 className="text-5xl md:text-6xl font-light font-serif tracking-tight text-foreground">
                Tritt mit dir in <br />
                <span className="text-primary italic">
                  Verbindung.
                </span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-sans">
                Bereit für deine Transformation? Kontaktiere mich für ein unverbindliches Kennenlernen. Ich freue mich darauf, dich auf deinem Weg zu innerer Klarheit zu begleiten.
              </p>
            </div>

            <div className="grid gap-6">
              {contactInfo.map((item, i) => <Card key={i} data-index={i} className="bg-secondary/20 border-border/50 backdrop-blur-sm hover:bg-secondary/30 transition-colors duration-300 overflow-hidden group rounded-sm">
                  <CardContent className="p-6 flex items-start gap-5">
                    <div className="shrink-0 size-12 rounded-sm bg-primary/5 flex items-center justify-center group-hover:scale-105 transition-transform duration-300 border border-primary/10">
                      <item.icon className="size-5 text-primary stroke-[1.5px]" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-medium text-muted-foreground text-xs uppercase tracking-widest font-sans">
                        {item.label}
                      </h3>
                      <Link className="block text-xl font-light text-foreground hover:text-primary transition-colors font-serif underline cursor-pointer" {...item.link}><Link to={item.href} newTab={item.label === "Standort"} className="![line-height:28px] !text-xl">
                        {item.value}
                      </Link></Link>
                      {item.href === "mailto:renata.wandelherz@gmail.com"}
                      <p className="text-xs text-muted-foreground/70 pt-1 font-sans">
                        {item.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/5 rounded-sm blur-2xl opacity-50 -z-10 transform scale-95 translate-y-4" />
              
              <div className="bg-white/80 backdrop-blur-xl border border-border/50 rounded-sm p-8 md:p-10 shadow-sm">
                <div className="mb-8">
                  <h3 className="text-2xl font-serif font-light text-foreground mb-2">Persönliche Anfrage</h3>
                  <p className="text-muted-foreground font-sans">Teile mir dein Anliegen mit und ich melde mich zeitnah bei dir.</p>
                </div>

                <FormProvider formId="contact_form_contact" sectionName="contact-section" defaultValues={{
                name: "",
                company: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
                data_privacy_consent: false
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                },
                data_privacy_consent: {
                  required: true
                }
              }} submitText="Nachricht senden" successMessage="Vielen Dank! Deine Nachricht wurde erfolgreich gesendet.">
                  <ContactFormContent />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}